import { useEffect, useRef, useState } from "react";
import ScriptTrackMockup from "./ScriptTrackMockup";
import { trackLandingEvent } from "../../lib/analytics";

type ProofStep = {
  label: string;
  title: string;
  body: string;
};

export default function ProofStory({
  pagePath,
  proofKind,
  eyebrow,
  headline,
  intro,
  steps,
  mockupVariant,
}: {
  pagePath: string;
  proofKind: "dscsa" | "software";
  eyebrow: string;
  headline: string;
  intro: string;
  steps: ProofStep[];
  mockupVariant: "compliance" | "inventory" | "dispensing";
}) {
  const rootRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let impressionSent = false;
    let engagementTimer: ReturnType<typeof setTimeout> | undefined;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || impressionSent) return;
      impressionSent = true;
      trackLandingEvent("landing_proof_impression", pagePath, {
        proofKind,
        presentation: reducedMotion ? "static_fallback" : "interactive_story",
      });
      if (reducedMotion) {
        trackLandingEvent("landing_proof_engagement", pagePath, {
          proofKind,
          action: "fallback_view",
        });
      }
      engagementTimer = setTimeout(() => {
        trackLandingEvent("landing_proof_engagement", pagePath, {
          proofKind,
          action: "narrative_8s",
        });
      }, 8000);
    }, { threshold: 0.35 });
    observer.observe(root);
    return () => {
      observer.disconnect();
      if (engagementTimer) clearTimeout(engagementTimer);
    };
  }, [pagePath, proofKind]);

  return (
    <section ref={rootRef} className="bg-primary py-16 lg:py-24 text-white landing-grid" aria-labelledby={`${proofKind}-proof-heading`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[0.88fr_1.12fr] gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-200">{eyebrow}</p>
            <h2 id={`${proofKind}-proof-heading`} className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">{headline}</h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-xl">{intro}</p>
            <ol className="mt-8 space-y-3" role="list" aria-label="Workflow sequence">
              {steps.map((step, index) => (
                <li key={step.label}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveStep(index);
                    trackLandingEvent("landing_proof_engagement", pagePath, {
                      proofKind,
                      action: "step_focus",
                      step: index + 1,
                    });
                  }}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    activeStep === index ? "border-accent bg-white/10" : "border-white/15 hover:border-white/35"
                  }`}
                  aria-pressed={activeStep === index}
                >
                  <span className="block text-[11px] font-bold uppercase tracking-widest text-accent-200">
                    {String(index + 1).padStart(2, "0")} / {step.label}
                  </span>
                  <span className="block mt-1.5 font-bold">{step.title}</span>
                  <span className="block mt-1 text-sm leading-relaxed text-white/65">{step.body}</span>
                </button>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-accent/15 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-[2rem] border border-white/15 bg-white p-5 sm:p-8 text-primary shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-accent">Illustrative workflow</p>
                  <p className="mt-1 text-sm text-gray-500">A product view, not a live customer account</p>
                </div>
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />
              </div>
              <ScriptTrackMockup variant={mockupVariant} compact />
              <div className="mt-5 border-t border-gray-100 pt-5" aria-live="polite">
                <p className="font-bold text-primary">{steps[activeStep].title}</p>
                <p className="mt-1 text-sm text-gray-600">{steps[activeStep].body}</p>
              </div>
            </div>
          </div>
        </div>
        <details className="mt-10 border-t border-white/15 pt-5 text-sm text-white/70">
          <summary
            className="cursor-pointer font-semibold text-white"
            onClick={() => trackLandingEvent("landing_proof_engagement", pagePath, { proofKind, action: "transcript_toggle" })}
          >
            Read the workflow transcript
          </summary>
          <ol className="mt-4 grid gap-3 sm:grid-cols-3">
            {steps.map((step) => <li key={step.label}><strong className="text-white">{step.title}:</strong> {step.body}</li>)}
          </ol>
        </details>
      </div>
    </section>
  );
}